<template>
  <div class="flex h-screen bg-[#f3f4f8]">
    <!-- Sidebar Component -->
    <DelivererSidebarNavigation :isOpen="sidebarOpen" @close="sidebarOpen = false"/>
      <!-- Overlay for mobile when sidebar open -->
    <div v-if="sidebarOpen" class="fixed inset-0 bg-black bg-opacity-40 z-20 md:hidden" @click="sidebarOpen = false">
    </div>

    <!-- Main Content -->
     <div class="flex-1 overflow-auto relative z-10 transition-all duration-300">
      <!-- Header with sidebar toggle -->
      <header class="bg-white shadow-sm sticky top-0 z-30">
        <div class="px-6 py-4 flex items-center justify-between">
          <!-- Left: Burger + Text -->
          <div class="flex items-center space-x-4">
            <!-- Sidebar toggle button (burger) -->
            <button @click="sidebarOpen = !sidebarOpen" class="md:hidden text-[#4d148c] focus:outline-none">
              <i class="fas fa-bars text-2xl"></i>
            </button>

            <!-- Header Text -->
            <div class="flex flex-col">
              <span class="text-[#4d148c] font-medium text-sm md:text-base">Traveler Dashboard</span>
              <h2 class="text-gray-500 text-sm mt-0.5">Overview</h2>
            </div>
          </div>

          <!-- Optional right side (if you want user/profile) -->
          <div class="hidden md:flex items-center space-x-4">
            <!-- Example: User avatar or actions -->
          </div>
        </div>
      </header>

      <div class="container mx-auto px-3 py-6">
        <!-- Error Message -->
        <div v-if="error" class="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          {{ error }}
        </div>

        <!-- Stats Grid -->
        <div class="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <!-- Available Items Card -->
          <div
            class="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-6 transform hover:scale-102">
            <div class="flex items-center">
              <div class="p-3 rounded-lg bg-blue-50 text-blue-600">
                <i class="fas fa-box-open text-xl"></i>
              </div>
              <div class="ml-4">
                <p class="text-sm text-gray-500 font-medium">Available Items</p>
                <div class="flex items-center">
                  <p class="text-2xl font-semibold text-gray-800">
                    {{ loading ? '-' : stats.available_items }}
                  </p>
                  <i v-if="loading" class="fas fa-circle-notch fa-spin ml-2 text-gray-400"></i>
                </div>
              </div>
            </div>
          </div>

          <!-- Ready to Pickup Items -->
          <div
            class="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-6 transform hover:scale-102">
            <div class="flex items-center">
              <div class="p-3 rounded-lg bg-purple-50 text-[#4d148c]">
                <i class="fas fa-hand-holding text-xl"></i>
              </div>
              <div class="ml-4">
                <p class="text-sm text-gray-500 font-medium">Ready to Pickup</p>
                <div class="flex items-center">
                  <p class="text-2xl font-semibold text-gray-800">
                    {{ loading ? '-' : stats.ready_to_pickup_items }}
                  </p>
                  <i v-if="loading" class="fas fa-circle-notch fa-spin ml-2 text-gray-400"></i>
                </div>
              </div>
            </div>
          </div>

          <!-- In Process Items -->
          <div
            class="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-6 transform hover:scale-102">
            <div class="flex items-center">
              <div class="p-3 rounded-lg bg-yellow-50 text-yellow-600">
                <i class="fas fa-shipping-fast text-xl"></i>
              </div>
              <div class="ml-4">
                <p class="text-sm text-gray-500 font-medium">In Process</p>
                <div class="flex items-center">
                  <p class="text-2xl font-semibold text-gray-800">
                    {{ loading ? '-' : stats.in_process_items }}
                  </p>
                  <i v-if="loading" class="fas fa-circle-notch fa-spin ml-2 text-gray-400"></i>
                </div>
              </div>
            </div>
          </div>

          <!-- Delivered Items -->
          <div
            class="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-6 transform hover:scale-102">
            <div class="flex items-center">
              <div class="p-3 rounded-lg bg-green-50 text-green-600">
                <i class="fas fa-check-circle text-xl"></i>
              </div>
              <div class="ml-4">
                <p class="text-sm text-gray-500 font-medium">Delivered</p>
                <div class="flex items-center">
                  <p class="text-2xl font-semibold text-gray-800">
                    {{ loading ? '-' : stats.delivered_items }}
                  </p>
                  <i v-if="loading" class="fas fa-circle-notch fa-spin ml-2 text-gray-400"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Current Deliveries Table -->
        <div class="mt-6 bg-white rounded-xl shadow-sm">
          <div class="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
            <h3 class="text-lg font-medium text-gray-800">Current Deliveries</h3>
            <router-link to="/my-deliveries"
              class="text-sm text-[#4d148c] hover:text-[#6d2ba8] flex items-center group">
              View All
              <i class="fas fa-arrow-right ml-1 group-hover:translate-x-1 transition-transform duration-300"></i>
            </router-link>
          </div>

          <!-- Loading State -->
          <div v-if="deliveriesLoading" class="p-6 text-center">
            <div class="animate-spin rounded-full h-8 w-8 border-2 border-[#4d148c] border-t-transparent mx-auto"></div>
            <p class="mt-2 text-sm text-gray-500">Loading deliveries...</p>
          </div>

          <!-- Error State -->
          <div v-else-if="deliveriesError" class="p-6">
            <div class="bg-red-50 text-red-600 p-4 rounded-lg flex items-center">
              <i class="fas fa-exclamation-circle mr-2"></i>
              {{ deliveriesError }}
            </div>
          </div>

          <!-- Table Content -->
          <div v-else class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item ID
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deadline
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Approval
                  </th>
                  <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Admin
                    Request</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="delivery in recentDeliveries" :key="delivery.id"
                  class="hover:bg-gray-50 transition-colors duration-200">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center">
                      <div class="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center mr-3">
                        <i class="fas fa-box text-gray-400"></i>
                      </div>
                      <div>
                        <span class="text-sm font-medium text-gray-900">{{ delivery.name }}</span>
                        <p class="text-xs text-gray-500">Qty: {{ delivery.quantity }}</p>
                        <!-- <p class="text-xs text-gray-500">ID: #{{ delivery.id }}</p> -->
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4 text-left   max-w-[150px] truncate text-sm text-gray-500">
                    <div class="flex items-center">
                      <span class="truncate " :title="delivery.id">{{ delivery.id }}</span>
                      <button @click="copyToClipboard(delivery.id)" class="text-blue-500 hover:text-blue-700">
                        <i class="fas fa-copy text-sm"></i>
                      </button>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {{ delivery.from_location }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {{ delivery.to_location }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {{ formatDate(delivery.delivery_deadline) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="getStatusClass(delivery.status)">
                      {{ formatStatus(delivery.status) }}
                    </span>
                  </td>
                  <td class="py-3 px-4 text-left capitalize" v-if="delivery.delivery_approval">
                    <span class="px-2 py-1 rounded text-xs font-medium" :class="delivery.delivery_approval === 'approved'
                      ? 'bg-green-100 text-green-600'
                      : delivery.delivery_approval === 'pending'
                        ? 'bg-yellow-100 text-yellow-600'
                        : 'bg-red-100 text-red-800'">
                      {{ delivery.delivery_approval }}
                    </span>
                  </td>

                  <td class="px-4 py-4 whitespace-nowrap text-sm text-center">
                    <template v-if="delivery.info_request_to_deliverer">
                      <button @click="openDialog(delivery)">
                        <span
                          class="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-semibold shadow transition-all duration-300"
                          :class="[
                            delivery.info_request_to_deliverer?.deliverer_response
                              ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white group-hover:scale-105'
                              : 'bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white animate-pulse group-hover:scale-105'
                          ]">
                          <i :class="[
                            'fas mr-2 transition-all',
                            delivery.info_request_to_deliverer?.deliverer_response
                              ? 'fa-check-circle'
                              : 'fa-exclamation-triangle'
                          ]"></i>
                          {{
                            delivery.info_request_to_deliverer?.deliverer_response
                              ? 'Information Requested'
                              : 'Action Required '
                          }}
                        </span>
                      </button>
                    </template>
                    <template v-else>
                      <span class="text-gray-400 text-xs">—</span>
                    </template>
                  </td>

                  <td class="px-6 py-4 whitespace-nowrap">
                    <button @click="viewDeliveryDetails(delivery.id)"
                      class="inline-flex items-center px-3 py-1.5 border border-[#4d148c] text-xs font-medium rounded-lg text-[#4d148c] hover:bg-[#4d148c] hover:text-white transition-colors duration-200">
                      <i class="fas fa-eye mr-1"></i>
                      View
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Admin Requested More Information Card -->
            <div v-if="showUserResponseDialog"
              class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-all duration-300">
              <div
                class="relative w-full max-w-3xl mx-4 md:mx-auto rounded-2xl p-8 pt-12 border-l-[6px] border-amber-500 bg-white shadow-2xl transform transition-all duration-300 hover:scale-[1.01">
                <!-- Cancel Button -->
                <button @click="showUserResponseDialog = false"
                  class="absolute top-3 right-3 text-gray-500 hover:text-red-600 transition-all text-xl">
                  <i class="fas fa-times"></i>
                </button>

                <!-- Header -->
                <div class="flex items-center mb-6">
                  <i :class="userResponseData.response_description ? 'fas fa-check-circle text-green-600' : 'fas fa-exclamation-triangle text-amber-600'"
                    class="mr-3 text-2xl"></i>
                  <h3 :class="userResponseData.response_description ? 'text-green-700' : 'text-amber-700'"
                    class="text-xl font-bold">
                    {{
                      userResponseData.response_description
                        ? 'Information Submitted'
                        : 'Action Required: Admin Requested Information'
                    }}
                  </h3>
                </div>


                <!-- Info Grid -->
                <div class="mb-6">
                  <!-- Admin Message -->
                  <div class="mb-6">
                    <label class="block text-sm font-semibold text-gray-500 mb-1">Admin Message</label>
                    <div
                      class="bg-amber-50 text-gray-800 font-medium rounded-lg px-4 py-3 border border-amber-200 shadow-sm">
                      {{ userResponseData.description }}
                    </div>

                    <!-- Requested File -->
                    <template v-if="userResponseData.request_file_path">
                      <label class="block text-sm font-semibold text-gray-500 mt-4 mb-1">Requested File</label>
                      <button @click="viewProofOfTravel(userResponseData.request_file_path)"
                        class="text-amber-700 font-semibold underline hover:text-amber-800 transition-all">
                        View Request File
                      </button>
                    </template>
                  </div>
                </div>

                <!-- Response Section -->
                <div v-if="userResponseData.response_description"
                  class="bg-gray-50 border border-amber-100 rounded-xl p-5 w-full shadow-sm">
                  <label class="block text-sm font-semibold text-gray-500 mb-1">Your Response</label>
                  <p class="text-gray-800 font-medium mb-2">
                    {{ userResponseData.response_description }}
                  </p>
                  <p class="text-xs text-gray-500 mb-2">
                    Submitted on {{ formatDateTime(userResponseData.response_at) }}
                  </p>

                  <!-- Response File -->
                  <template v-if="userResponseData.response_file_path">
                    <label class="block text-sm font-semibold text-gray-500 mt-3 mb-1">Response File</label>
                    <button @click="viewProofOfTravel(userResponseData.response_file_path)"
                      class="text-amber-700 font-semibold underline hover:text-amber-800 transition-all">
                      View Response File
                    </button>
                  </template>
                </div>


                <!-- Respond / Resubmit Button -->
                <div class="mt-6 flex justify-end" v-if="modelItem.delivery_approval === 'pending'">
                  <button @click="openResponseDialog"
                    class="px-4 py-2 rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-all">
                    <i class="fas fa-reply mr-2"></i>
                    {{ userResponseData.response_description ? 'Resubmit Response' : 'Respond to Request' }}
                  </button>
                </div>

                <!-- More Info Dialog -->
                <dialog ref="moreInfoDialog" class="rounded-xl p-6 bg-white w-full md:max-w-md shadow-xl border">
                  <div class="flex justify-between items-center mb-4">
                    <h3 class="text-lg font-semibold text-[#4d148c]">Submit Your Response</h3>
                    <button @click="closeResponseDialog"
                      class="absolute top-3 right-3 text-gray-500 hover:text-red-600 transition-all text-xl">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>

                  <div class="space-y-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Message</label>
                      <textarea v-model="moreInfoMessage" rows="4"
                        class="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-[#4d148c]"></textarea>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Attach Proof (optional)</label>
                      <input type="file" accept=".pdf, .jpeg, .jpg, .png" @change="handleFileChange"
                        class="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#4d148c] file:text-white hover:file:bg-[#3a0e6c]" />
                      <p class="mt-1 text-xs text-gray-500">
                        Accepted formats: PDF, JPEG, PNG. Maximum size: 2MB.
                      </p>
                    </div>
                  </div>

                  <div class="mt-6 flex justify-end space-x-4">
                    <button @click="closeResponseDialog"
                      class="px-4 py-2 rounded-md border border-gray-400 text-gray-700 hover:bg-gray-100">
                      Cancel
                    </button>
                    <button @click="submitMoreInfoRequest"
                      class="px-4 py-2 rounded-md bg-amber-500 text-white hover:bg-amber-600">
                      Send Response
                    </button>
                  </div>
                </dialog>
              </div>
            </div>

            <!-- Proof of Travel Modal -->
            <div v-if="showProofModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title"
              role="dialog" aria-modal="true">
              <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">

                <!-- Background overlay -->
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeProofModal">
                </div>

                <!-- Modal panel -->
                <div
                  class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
                  <div class="absolute top-0 right-0 pt-4 pr-4">
                    <button @click="closeProofModal" class="text-gray-400 hover:text-gray-500 focus:outline-none">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>

                  <!-- Modal Content -->
                  <div class="sm:flex sm:items-start">
                    <div class="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <h3 class="text-lg leading-6 font-medium text-gray-900">
                        Proof of Travel Document
                      </h3>

                      <div class="mt-4">
                        <!-- Loading State -->
                        <div v-if="proofLoading" class="flex justify-center py-8">
                          <i class="fas fa-circle-notch fa-spin text-2xl text-[#4d148c]"></i>
                        </div>

                        <!-- Error State -->
                        <div v-else-if="proofError" class="bg-red-50 text-red-600 p-4 rounded-lg">
                          {{ proofError }}
                        </div>

                        <!-- Document Preview -->
                        <div v-else-if="proofDocument" class="rounded-lg overflow-hidden">
                          <object :data="proofDocument.url" type="application/pdf" class="w-full h-[600px]">
                            <embed :src="proofDocument.url" type="application/pdf" class="w-full h-[600px]">
                            <p class="text-center py-4 text-gray-600">
                              Unable to display PDF. <a :href="proofDocument.url" target="_blank"
                                class="text-[#4d148c] hover:underline">Click here to view</a>
                            </p>
                          </object>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-if="recentDeliveries.length === 0" class="p-8 text-center">
              <p class="text-gray-500">No active deliveries</p>
              <router-link to="/available-items"
                class="mt-4 inline-flex items-center text-sm text-[#4d148c] hover:text-[#6d2ba8]">
                Browse available items
                <i class="fas fa-arrow-right ml-1"></i>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../services/api';
import DelivererSidebarNavigation from '@/components/DelivererSidebar.vue';
import { useToast } from 'vue-toastification';
export default {
  name: 'DelivererDashboardPage',
  components: {
    DelivererSidebarNavigation
  },
  data() {
    return {
      stats: {
        available_items: 0,
        in_process_items: 0,
        delivered_items: 0,
        ready_to_pickup_items: 0
      },
      deliveries: [],
      deliveriesLoading: false,
      deliveriesError: null,
      loading: false,
      error: null,
      userResponseData: {
        description: "",
        request_file_path: null,
        response_description: "",
        response_file_path: null,
        response_at: null,
        item_id: null
      },
      showUserResponseDialog: false,
      showProofModal: false,
      proofLoading: (false),
      proofError: '',
      proofDocument: null,

      //Reponse request Dialog
      moreInfoMessage: '',
      selectedFile: null,
      modelItem: null,
      intervalId: null,

      //User location 
      location: null,
      locationError: null,
      sidebarOpen: false
    }
  },
  computed: {
    recentDeliveries() {
      return this.deliveries.slice(0, 5); // Only show first 5 deliveries
    }
  },
  created() {
    this.loadStats();
    this.loadDeliveries(true);
  },
  async mounted() {

    // Poll every 30 seconds, but only if tab is active
    this.intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        this.loadStats()
        this.loadDeliveries(false)
      }
    }, 30000)

    // Listen to tab visibility change
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },

  beforeUnmount() {
    // Clean up
    clearInterval(this.intervalId)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  },
  methods: {
    getStatusClass(status) {
      const classes = {
        pending: 'px-3 py-1 text-xs font-medium rounded-full bg-yellow-50 text-yellow-700',
        accepted: 'px-3 py-1 text-xs font-medium rounded-full bg-green-50 text-green-700',
        rejected: 'px-3 py-1 text-xs font-medium rounded-full bg-red-50 text-red-700',
        'ready_to_pickup': 'px-3 py-1 text-xs font-medium rounded-full bg-purple-50 text-purple-700',
        completed: 'px-3 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700',
        in_process: 'px-3 py-1 text-xs font-medium rounded-full bg-yellow-50 text-yellow-700',
        delivered: 'px-3 py-1 text-xs font-medium rounded-full bg-green-50 text-green-700',
        cancelled: 'px-3 py-1 text-xs font-medium rounded-full bg-red-50 text-red-700'
      };
      return classes[status.toLowerCase()] || 'px-3 py-1 text-xs font-medium rounded-full bg-gray-50 text-gray-700';
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    formatStatus(status) {
      if (!status) return '';
      return status
        .toLowerCase()
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    async loadStats() {
      this.loading = true;
      this.error = null;

      try {
        const response = await api.get('/api/deliverer-stats');
        if (response.data.success) {
          this.stats = response.data.data;
        }
      } catch (error) {
        console.error('Error loading stats:', error);
        this.error = error.response?.data?.message || 'Failed to load statistics';
      } finally {
        this.loading = false;
      }
    },
    async loadDeliveries(showLoading) {
      try {
        if (showLoading) {
          this.deliveriesLoading = true;
        }
        this.deliveriesError = null;

        const response = await api.get('/api/my-deliveries', {
          params: {
            per_page: 5,
            page: 1
          }
        });

        if (response.data.success) {
          this.deliveries = response.data.data.items;
        }
      } catch (error) {
        console.error('Error loading deliveries:', error);
        this.deliveriesError = error.response?.data?.message || 'Failed to load deliveries';
      } finally {
        if (showLoading) {
          this.deliveriesLoading = false;
        }
      }
    },
    viewDeliveryDetails(id) {
      this.$router.push({
        name: 'DeliveryItemDetail',
        params: { id }
      });
    },
    openDialog(item) {
      this.modelItem = item;
      this.showUserResponseDialog = true;
      this.userResponseData = {
        description: item.info_request_to_deliverer?.description,
        request_file_path: item.info_request_to_deliverer?.request_file_path,
        response_description: item.info_request_to_deliverer?.deliverer_response || "",
        response_file_path: item.info_request_to_deliverer?.response_file_path || null,
        response_at: item.info_request_to_deliverer?.response_at || null,
        item_id: item.id
      };
    },
    async viewProofOfTravel(file_path) {
      this.showProofModal = true;
      this.proofLoading = true;
      this.proofError = null;
      this.proofDocument = null;

      try {
        // Create blob URL for the streamed response
        const response = await api.get(`/api/proof-of-travel/${file_path}`, {
          responseType: 'blob'  // Important: Set responseType to 'blob'
        });

        // Create blob URL
        const blob = new Blob([response.data], {
          type: response.headers['content-type']
        });
        const url = URL.createObjectURL(blob);

        this.proofDocument = {
          url: url,
          type: response.headers['content-type']?.includes('pdf') ? 'pdf' : 'image'
        };
      } catch (error) {
        this.proofError = error.response?.data?.message || error.message || 'Failed to load proof of travel document';
        console.error('Error loading proof of travel:', error);
      } finally {
        this.proofLoading = false;
      }
    },
    formatDateTime(dateString) {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    openResponseDialog() {
      this.moreInfoMessage = '';
      this.selectedFile = null;
      const dialog = this.$refs.moreInfoDialog;
      if (dialog) {
        if (dialog.open) dialog.close(); // ensure clean open
        dialog.showModal();
      }
    },
    closeResponseDialog() {
      const dialog = this.$refs.moreInfoDialog;
      if (dialog && dialog.open) dialog.close();
      this.moreInfoMessage = '';
      this.selectedFile = null;
    },
    handleFileChange(event) {
      const toast = useToast();
      const file = event.target.files[0];

      if (file) {
        const maxSizeInBytes = 2 * 1024 * 1024; // 2MB
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];

        if (!allowedTypes.includes(file.type)) {
          toast.warning('Please upload a PDF, JPEG, or PNG file.');
          this.selectedFile = null;
          event.target.value = '';
          return;
        }

        if (file.size > maxSizeInBytes) {
          toast.warning('File size must be less than or equal to 2MB.');
          this.selectedFile = null;
          event.target.value = '';
          return;
        }

        this.selectedFile = file;
      }
    },
    async submitMoreInfoRequest() {
      const toast = useToast();
      const formData = new FormData();
      formData.append('item_id', this.userResponseData.item_id);
      formData.append('description', this.moreInfoMessage);
      formData.append('response_by', 'deliverer'); // Assuming 'sender' is the user type
      if (this.selectedFile) {
        formData.append('file', this.selectedFile);
      }

      try {
        await api.post('/api/respond-further-info', formData);
        toast.success('Information sent successfully!');
        this.closeResponseDialog();
        this.showUserResponseDialog = false;
        this.loadDeliveries(true)
        // Optional: reload or refetch item
      } catch (error) {
        console.error(error);
        toast.error('Failed to send information.');
      }
    },
    closeProofModal() {
      this.showProofModal = false
      this.proofDocument = null
      this.proofError = ''
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.loadStats()
        this.loadDeliveries(false)
      }
    },
    copyToClipboard(text) {
      const toast = useToast()
      navigator.clipboard.writeText(text).then(() => {
        toast.success('Copied to clipboard!');
      });
    }
  }
}
</script>

<style scoped>
@keyframes slideInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.animate-slideInDown {
  animation: slideInDown 0.6s ease-out forwards;
}

.animate-fadeIn {
  animation: fadeIn 0.6s ease-out forwards;
}

.overflow-auto {
  scroll-behavior: smooth;
}
</style>